import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

// Educational Icons
const DollarSignIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const CreditCardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
    <line x1="1" y1="10" x2="23" y2="10"></line>
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
);

const BuildingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
    <path d="M6 12h4v4h-4v-4"></path>
    <path d="M14 12h4v4h-4v-4"></path>
    <path d="M6 20h4v2h-4v-2"></path>
    <path d="M14 20h4v2h-4v-2"></path>
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="M21 21l-4.35-4.35"></path>
  </svg>
);

const FilterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7,10 12,15 17,10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const RefreshIcon = ({ spinning }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{
      animation: spinning ? 'spin 1s linear infinite' : 'none',
    }}
  >
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const StatCard = ({ title, value, subtitle, icon: Icon, color, bgColor }) => {
  return (
    <div style={{
      background: bgColor,
      borderRadius: '12px',
      padding: '20px',
      color: 'white',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{
          padding: '8px',
          borderRadius: '8px',
          backgroundColor: 'rgba(255,255,255,0.2)'
        }}>
          <Icon />
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '4px' }}>
        {value}
      </div>
      <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: '12px', opacity: 0.7 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
};

const PaymentReports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [filters, setFilters] = useState({
    campusId: '',
    courseId: '',
    startDate: '',
    endDate: '',
    status: '',
    paymentMethod: ''
  });
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const [campusesRes, coursesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/campuses`, { headers }),
        fetch(`${API_BASE_URL}/courses`, { headers })
      ]);

      if (campusesRes.ok) {
        const campusesData = await campusesRes.json();
        setCampuses(campusesData);
      }

      if (coursesRes.ok) {
        const coursesData = await coursesRes.json();
        setCourses(coursesData);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch payment analytics
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      // For now, we'll aggregate data from payments endpoint
      // In a real implementation, you'd have dedicated analytics endpoints
      const response = await fetch(`${API_BASE_URL}/payments`, { headers });

      if (response.ok) {
        const payments = await response.json();

        // Calculate analytics from payment data
        const totalRevenue = payments
          .filter(p => p.status === 'confirmed')
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        const confirmedPayments = payments.filter(p => p.status === 'confirmed').length;
        const pendingPayments = payments.filter(p => p.status === 'pending').length;
        const rejectedPayments = payments.filter(p => p.status === 'rejected').length;

        const mpesaPayments = payments.filter(p => p.paymentMethod === 'mpesa' && p.status === 'confirmed').length;
        const cashPayments = payments.filter(p => p.paymentMethod === 'cash' && p.status === 'confirmed').length;

        // Revenue by payment method
        const mpesaRevenue = payments
          .filter(p => p.paymentMethod === 'mpesa' && p.status === 'confirmed')
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        const cashRevenue = payments
          .filter(p => p.paymentMethod === 'cash' && p.status === 'confirmed')
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        // Campus revenue breakdown
        const campusRevenue = {};
        payments
          .filter(p => p.status === 'confirmed')
          .forEach(payment => {
            const campusName = (payment.Student?.Campus?.name || payment.student?.campus?.name || 'Unknown');
            campusRevenue[campusName] = (campusRevenue[campusName] || 0) + parseFloat(payment.amount || 0);
          });

        // Monthly trends (simplified - last 6 months)
        const monthlyTrends = [];
        for (let i = 5; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

          // Filter payments for this month
          const monthPayments = payments.filter(p => {
            const paymentDate = new Date(p.createdAt);
            return paymentDate.getFullYear() === date.getFullYear() &&
                   paymentDate.getMonth() === date.getMonth() &&
                   p.status === 'confirmed';
          });

          const monthRevenue = monthPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

          monthlyTrends.push({
            month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            revenue: monthRevenue,
            transactions: monthPayments.length,
            mpesa: monthPayments.filter(p => p.paymentMethod === 'mpesa').length,
            cash: monthPayments.filter(p => p.paymentMethod === 'cash').length
          });
        }

        setAnalytics({
          summary: {
            totalRevenue,
            confirmedPayments,
            pendingPayments,
            rejectedPayments,
            mpesaPayments,
            cashPayments,
            mpesaRevenue,
            cashRevenue,
            successRate: confirmedPayments > 0 ? ((confirmedPayments / (confirmedPayments + pendingPayments + rejectedPayments)) * 100).toFixed(1) : 0
          },
          monthlyTrends,
          campusRevenue: Object.entries(campusRevenue).map(([name, revenue]) => ({
            name,
            revenue,
            transactions: payments.filter(p => (p.Student?.Campus?.name || p.student?.campus?.name) === name && p.status === 'confirmed').length
          })),
          paymentMethodData: [
            { name: 'M-Pesa', value: mpesaRevenue, count: mpesaPayments, color: '#10B981' },
            { name: 'Cash', value: cashRevenue, count: cashPayments, color: '#F59E0B' }
          ]
        });
      } else {
        console.error('Failed to fetch payments');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch payment records
  const fetchPaymentRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const params = new URLSearchParams();
      if (filters.campusId) params.append('campusId', filters.campusId);
      if (filters.courseId) params.append('courseId', filters.courseId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.status) params.append('status', filters.status);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      params.append('limit', '100');

      const response = await fetch(`${API_BASE_URL}/payments?${params}`, { headers });

      if (response.ok) {
        const data = await response.json();
        setPaymentRecords(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching payment records:', error);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchAnalytics();
    fetchPaymentRecords();
  }, [filters]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchAnalytics(), fetchPaymentRecords()]);
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleExport = () => {
    const csvData = paymentRecords.map(record => ({
      Date: new Date(record.createdAt).toLocaleDateString(),
      Student: record.Student?.name || record.student?.name || 'Unknown',
      'Reg Number': record.Student?.regNumber || record.student?.regNumber || '',
      Unit: record.Unit?.name || record.unit?.name || 'Unknown',
      Course: record.Unit?.Course?.name || record.unit?.course?.name || 'Unknown',
      Campus: (record.Student?.Campus?.name || record.student?.campus?.name || 'Unknown'),
      Amount: record.amount,
      'Payment Method': record.paymentMethod,
      Status: record.status,
      'Reference Number': record.referenceNumber || ''
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'rejected': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'confirmed': return '#D1FAE5';
      case 'pending': return '#FEF3C7';
      case 'rejected': return '#FEE2E2';
      default: return '#F3F4F6';
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'mpesa': return '#10B981';
      case 'cash': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  const getMethodBgColor = (method) => {
    switch (method) {
      case 'mpesa': return '#D1FAE5';
      case 'cash': return '#FEF3C7';
      default: return '#F3F4F6';
    }
  };

  if (loading && !analytics) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '60px',
        backgroundColor: 'white',
        borderRadius: '16px',
        margin: '20px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #F3F4F6',
          borderTop: '4px solid #8B5CF6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginRight: '16px',
        }} />
        <span style={{ fontSize: '16px', color: '#6B7280' }}>Loading payment reports...</span>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 50%, #E2E8F0 100%)',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #8B5CF6 30%, #06B6D4 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: '0 0 8px 0',
            }}>
              Payment Reports
            </h1>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '16px' }}>
              Comprehensive financial analytics and payment tracking
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleRefresh}
              style={{
                padding: '12px 16px',
                backgroundColor: '#F3F4F6',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
            >
              <RefreshIcon spinning={refreshing} />
              Refresh
            </button>

            <button
              onClick={handleExport}
              style={{
                padding: '12px 16px',
                backgroundColor: '#8B5CF6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#7C3AED'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#8B5CF6'}
            >
              <DownloadIcon />
              Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          padding: '20px',
          backgroundColor: '#F9FAFB',
          borderRadius: '12px',
        }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Campus
            </label>
            <select
              value={filters.campusId}
              onChange={(e) => setFilters(prev => ({ ...prev, campusId: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                outline: 'none',
              }}
            >
              <option value="">All Campuses</option>
              {campuses.map(campus => (
                <option key={campus.id} value={campus.id}>{campus.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Course
            </label>
            <select
              value={filters.courseId}
              onChange={(e) => setFilters(prev => ({ ...prev, courseId: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                outline: 'none',
              }}
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                outline: 'none',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                outline: 'none',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                outline: 'none',
              }}
            >
              <option value="">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
              Payment Method
            </label>
            <select
              value={filters.paymentMethod}
              onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                outline: 'none',
              }}
            >
              <option value="">All Methods</option>
              <option value="mpesa">M-Pesa</option>
              <option value="cash">Cash</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {analytics && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '24px'
        }}>
          <StatCard
            title="Total Revenue"
            value={`KSH ${analytics.summary.totalRevenue.toLocaleString()}`}
            subtitle="Confirmed payments"
            icon={DollarSignIcon}
            color="#10B981"
            bgColor="linear-gradient(135deg, #10B981 0%, #34D399 100%)"
          />
          <StatCard
            title="Confirmed Payments"
            value={analytics.summary.confirmedPayments.toLocaleString()}
            subtitle={`${analytics.summary.successRate}% success rate`}
            icon={CreditCardIcon}
            color="#8B5CF6"
            bgColor="linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)"
          />
          <StatCard
            title="M-Pesa Revenue"
            value={`KSH ${analytics.summary.mpesaRevenue.toLocaleString()}`}
            subtitle={`${analytics.summary.mpesaPayments} transactions`}
            icon={TrendingUpIcon}
            color="#06B6D4"
            bgColor="linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)"
          />
          <StatCard
            title="Cash Revenue"
            value={`KSH ${analytics.summary.cashRevenue.toLocaleString()}`}
            subtitle={`${analytics.summary.cashPayments} transactions`}
            icon={BuildingIcon}
            color="#F59E0B"
            bgColor="linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)"
          />
        </div>
      )}

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Revenue Trends */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            Revenue Trends
          </h3>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics?.monthlyTrends || []}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white'
                  }}
                  formatter={(value) => [`KSH ${value.toLocaleString()}`, 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10B981"
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment Methods */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            Payment Methods
          </h3>
          <div style={{ height: '200px', marginBottom: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={analytics?.paymentMethodData || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {analytics?.paymentMethodData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `KSH ${value.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {analytics?.paymentMethodData?.map((method, index) => (
              <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: method.color,
                  }} />
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    {method.name}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    KSH {method.value.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    {method.count} transactions
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Campus Revenue */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
          Campus Revenue Breakdown
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          {analytics?.campusRevenue?.map((campus, index) => (
            <div key={index} style={{
              padding: '20px',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              backgroundColor: '#F9FAFB'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  {campus.name}
                </h4>
                <div style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: '#D1FAE5',
                  color: '#065F46'
                }}>
                  {campus.transactions} txns
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Revenue</div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
                  KSH {campus.revenue.toLocaleString()}
                </div>
              </div>

              <div style={{ backgroundColor: '#E5E7EB', borderRadius: '4px', height: '6px' }}>
                <div style={{
                  backgroundColor: '#10B981',
                  height: '100%',
                  borderRadius: '4px',
                  width: `${analytics.summary.totalRevenue > 0 ? (campus.revenue / analytics.summary.totalRevenue) * 100 : 0}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Payment Records */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
          Recent Payment Records
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Student</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Unit</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Method</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {paymentRecords.slice(0, 20).map((record, index) => (
                <tr key={record.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '12px', color: '#374151' }}>
                    {new Date(record.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', fontWeight: '500', color: '#374151' }}>
                    {record.Student?.name || record.student?.name || 'Unknown'}
                  </td>
                  <td style={{ padding: '12px', color: '#6B7280' }}>
                    {record.Unit?.name || record.unit?.name || 'Unknown'}
                  </td>
                  <td style={{ padding: '12px', fontWeight: '600', color: '#111827' }}>
                    KSH {parseFloat(record.amount || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: getMethodBgColor(record.paymentMethod),
                      color: getMethodColor(record.paymentMethod)
                    }}>
                      {record.paymentMethod}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: getStatusBgColor(record.status),
                      color: getStatusColor(record.status)
                    }}>
                      {record.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PaymentReports;
